import { useState } from 'react';
import {
  H2,
  TabContent,
  TabHeader,
  Tabs,
  TabsContents,
  TabsHeaders,
} from '@vp/swan';
import { useTranslations } from '@vp/om-translations';
import { BuyItAgainView } from './buyItAgainTab/BuyItAgainView';
import {
  BUY_IT_AGAIN_TAB,
  ORDER_HISTORY_TAB,
} from '../constants/orderHistoryTabs';
import { useSiteData } from '../hooks/useSiteData';
import { useQueryParameter } from '@vp/common-ubik-utils';
import { REORDER_CTA_QUERY_PARAM } from '../constants/queryParams';
import {
  trackBuyItAgainPageChanged,
  trackOrderHistoryExperienceTabChanged,
} from '../apis/newrelic/newRelicTrackingApi';
import {
  BUY_IT_AGAIN_PAGE_NAME,
  BUY_IT_AGAIN_SOURCE_PAGE,
  ORDER_HISTORY_PAGE_NAME,
  ORDER_HISTORY_SOURCE_PAGE,
} from '../constants/sourcePage';
import { PageUxModWrapper } from './uxMods/PageUxModWrapper';
import { PaginationProvider } from './contexts/PaginationContext';
import {
  BUY_IT_AGAIN_DEFAULT_PAGE_SIZE,
  ORDER_HISTORY_DEFAULT_PAGE_SIZE,
} from '../constants/pagination';
import { BUY_IT_AGAIN_HOST, ORDER_HISTORY_HOST } from '../constants/uxMods';
import { useShopperId } from '../hooks/useShopperId';
import { DebugTools } from './debugTooling/DebugTools';
import { useDebugModeContext } from './contexts/DebugModeContext';
import { OrderHistoryView } from './orderHistoryTab/OrderHistoryView';
import { SourcePageProvider } from './contexts/SourcePageContext';

export const OrderHistoryApplication = () => {
  const reorderCtaQueryValue = useQueryParameter(REORDER_CTA_QUERY_PARAM);

  // show the reorder cta tab if query param is ?reorderCta=true or just ?reorderCta
  const isReorderCtaNavigation =
    reorderCtaQueryValue === 'true' ||
    typeof reorderCtaQueryValue === 'undefined';

  const { siteName, vistacartTenant: vistacartTenantName } = useSiteData();
  const shopperId = useShopperId();
  const { debugModeEnabled } = useDebugModeContext();
  const [orderHistoryMessages = {}, buyItAgainMessages = {}] = useTranslations([
    'orderHistoryMessages',
    'buyItAgainMessages',
  ]);

  const defaultSelectedTabId = isReorderCtaNavigation
    ? BUY_IT_AGAIN_TAB
    : ORDER_HISTORY_TAB;
  const [selectedTabId, setSelectedTabId] = useState(defaultSelectedTabId);

  const handleRequestTabChange = (requestedTabId: string | null) => {
    // this really shouldn't happen, but the requestedTabId swan returns can be null
    if (!requestedTabId) {
      return;
    }

    setSelectedTabId(requestedTabId);
    trackOrderHistoryExperienceTabChanged({
      selectedTabId:
        requestedTabId === BUY_IT_AGAIN_TAB
          ? BUY_IT_AGAIN_SOURCE_PAGE
          : ORDER_HISTORY_SOURCE_PAGE,
      shopperId,
      siteName,
      tenant: vistacartTenantName,
    });
  };

  return (
    <>
      {debugModeEnabled && <DebugTools />}
      <Tabs
        mt={{ xs: 8, sm: 10, md: 10, lg: 10 }}
        selectedTabId={selectedTabId}
        onRequestTabChange={(requestedTabId) => {
          handleRequestTabChange(requestedTabId);
        }}
        aria-label={orderHistoryMessages.navigationTabsLabel}
      >
        <TabsHeaders>
          <TabHeader tabId={BUY_IT_AGAIN_TAB}>
            {buyItAgainMessages.titleMessage}
          </TabHeader>
          <TabHeader tabId={ORDER_HISTORY_TAB}>
            {orderHistoryMessages.titleMessage}
          </TabHeader>
        </TabsHeaders>
        <TabsContents>
          <TabContent tabId={BUY_IT_AGAIN_TAB}>
            <SourcePageProvider sourcePage={BUY_IT_AGAIN_SOURCE_PAGE}>
              <PageUxModWrapper modHost={BUY_IT_AGAIN_HOST}>
                <PaginationProvider
                  defaultItemsPerPage={BUY_IT_AGAIN_DEFAULT_PAGE_SIZE}
                  onPageChangeTrackingFn={trackBuyItAgainPageChanged}
                >
                  {selectedTabId === BUY_IT_AGAIN_TAB && (
                    <>
                      {/* set page name for tracking on Buy It Again page */}
                      <meta name="pageName" content={BUY_IT_AGAIN_PAGE_NAME} />
                      <H2
                        visuallyHidden
                        data-testid="hidden-buy-it-again-heading"
                      >
                        {buyItAgainMessages.titleMessage}
                      </H2>
                      <BuyItAgainView
                        handleRequestTabChange={handleRequestTabChange}
                      />
                    </>
                  )}
                </PaginationProvider>
              </PageUxModWrapper>
            </SourcePageProvider>
          </TabContent>
          <TabContent tabId={ORDER_HISTORY_TAB}>
            <SourcePageProvider sourcePage={ORDER_HISTORY_SOURCE_PAGE}>
              <PageUxModWrapper modHost={ORDER_HISTORY_HOST}>
                <PaginationProvider
                  defaultItemsPerPage={ORDER_HISTORY_DEFAULT_PAGE_SIZE}
                >
                  {selectedTabId === ORDER_HISTORY_TAB && (
                    <>
                      {/* set page name for tracking on Order History page */}
                      <meta name="pageName" content={ORDER_HISTORY_PAGE_NAME} />
                      <H2
                        visuallyHidden
                        data-testid="hidden-order-history-heading"
                      >
                        {orderHistoryMessages.titleMessage}
                      </H2>
                      <OrderHistoryView />
                    </>
                  )}
                </PaginationProvider>
              </PageUxModWrapper>
            </SourcePageProvider>
          </TabContent>
        </TabsContents>
      </Tabs>
    </>
  );
};
